import BlockBase from '@components/dnd/base/blockBase';
import { ColumnComp } from '@components/trace/column/column.comp';
import { EditorMode } from '@components/trace/column/editorMode.column';
import { useAtom } from '@hooks/useAtom';
import EndControlsFB, {
  type SettingsConfig
} from '@library/formBuilder/endControls';
import { NumberEditor } from '@library/tableEditor/editors/number.editor';
import { type DNDBlock, ResolvedVariableType } from '@stratumn/studio-shared';
import { NumberWidget } from '@widgets/number/number.widget';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { ParamsList } from '@/routes/routes.types';

import { initialNumberData } from './number.data';
import { NumberSettingsModal } from './number.settings';
import { NumberIcon } from './number.utils';

const NumberTableEditorBlock = (block: DNDBlock) => {
  const actionId = useParams()[ParamsList.ActionId] as string;

  const atom = useAtom({
    type: 'Table_Editor_Number',
    sourceId: block.id,
    parentId: actionId,
    parentKind: 'table_editor',
    initialData: initialNumberData,
    variableInfo: {
      resolvedType: ResolvedVariableType.Number
    }
  });

  if (!atom) {
    return null;
  }

  const getModalSettings = (): SettingsConfig => ({
    title: atom.data.title || 'Number Column Title',
    content: <NumberSettingsModal atom={atom} />
  });

  const getEndControls = () => (
    <EndControlsFB
      dataItem={atom}
      dndBlock={block}
      hasRequired
      settings={getModalSettings()}
    />
  );

  return (
    <BlockBase
      dataItem={atom}
      dndBlock={block}
      endControls={getEndControls()}
      icon={NumberIcon}
      title={atom.data.title}
    >
      <ColumnComp
        atomId={atom.id}
        isDisplayedByDefault={atom.data.isDisplayedByDefault}
        isfixedColumn={atom.data.isfixedColumn}
        onDisplayByDefaultChange={v => (atom.data.isDisplayedByDefault = v)}
        onFixedColumnChange={v => (atom.data.isfixedColumn = v)}
        onTitleChange={v => (atom.data.title = v)}
        onWidthChange={v => (atom.data.width = v)}
        title={atom.data.title}
        width={atom.data.width}
      />
      <EditorMode
        atomId={atom.id}
        canSelectEditorType={false}
        isEditMode={atom.data.isEditMode}
        onIsEditModeChange={v => (atom.data.isEditMode = v)}
      />
      {atom.data.isEditMode ? (
        <NumberEditor atom={atom} />
      ) : (
        <NumberWidget
          atomId={atom.id}
          onLocaleChange={v => (atom.data.cell.view.format.locale = v)}
          onTabRightChange={v => (atom.data.cell.view.tabRight = v)}
          selectedLocale={atom.data.cell.view.format.locale}
          tabRight={atom.data.cell.view.tabRight}
        />
      )}
    </BlockBase>
  );
};

export default observer(NumberTableEditorBlock);
